import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchMessages, sendMessage, clearCurrentMessages, markAsRead, setActiveConversation, refreshMessages } from '../store/slices/messagesSlice';
import { wsService } from '../services/websocket';
import './Chat.css';

const Chat: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentMessages, messagesLoading, messagesTotal } = useAppSelector((state) => state.messages);
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  useEffect(() => {
    if (id) {
      dispatch(setActiveConversation(id));
      dispatch(clearCurrentMessages());
      setPage(1);
      dispatch(fetchMessages({ conversationId: id, page: 1 }));
      dispatch(markAsRead(id));
    }
    return () => {
      dispatch(setActiveConversation(null));
      dispatch(clearCurrentMessages());
    };
  }, [dispatch, id]);

  // Listen for new messages in this conversation via WS
  useEffect(() => {
    const handleNewMessage = (data: any) => {
      if (data.conversation_id === id) {
        // Mark as read since we're viewing this conversation
        dispatch(markAsRead(id!));
      }
    };

    wsService.on('new_message', handleNewMessage);
    return () => {
      wsService.off('new_message', handleNewMessage);
    };
  }, [dispatch, id]);

  // Poll for new messages as a fallback in case WebSocket drops
  useEffect(() => {
    if (!id) return;
    const interval = setInterval(() => {
      dispatch(refreshMessages({ conversationId: id }));
    }, 3000);
    return () => clearInterval(interval);
  }, [dispatch, id]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentMessages, shouldAutoScroll]);

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Auto-scroll if near bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShouldAutoScroll(distanceFromBottom < 100);

    // Load older messages when scrolled to top
    if (container.scrollTop === 0 && !messagesLoading && currentMessages.length < messagesTotal) {
      const nextPage = page + 1;
      setPage(nextPage);
      const prevHeight = container.scrollHeight;
      dispatch(fetchMessages({ conversationId: id!, page: nextPage })).then(() => {
        // Maintain scroll position after prepending
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight - prevHeight;
        });
      });
    }
  }, [dispatch, id, messagesLoading, currentMessages.length, messagesTotal, page]);

  const handleSend = async () => {
    if (!newMessage.trim() || !id || sending) return;

    setSending(true);
    setShouldAutoScroll(true);
    try {
      await dispatch(sendMessage({ conversationId: id, content: newMessage.trim() })).unwrap();
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
    }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Group messages by date
  const getDateKey = (dateString: string) => new Date(dateString).toDateString();

  let lastDateKey = '';

  return (
    <div className="chat">
      <header className="chat-header">
        <Link to="/messages" className="back-btn">&larr;</Link>
        <h2>Chat</h2>
      </header>

      <div
        className="chat-messages"
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {messagesLoading && page === 1 && (
          <div className="loading-state">Loading messages...</div>
        )}

        {!messagesLoading && currentMessages.length === 0 && (
          <div className="empty-state">
            <p>No messages yet. Say hello!</p>
          </div>
        )}

        {currentMessages.map((msg) => {
          const dateKey = getDateKey(msg.created_at);
          const showDate = dateKey !== lastDateKey;
          lastDateKey = dateKey;
          const isMine = msg.sender_id === currentUser?.id;

          return (
            <React.Fragment key={msg.id}>
              {showDate && (
                <div className="chat-date-divider">
                  <span>{formatDate(msg.created_at)}</span>
                </div>
              )}
              <div className={`chat-message ${isMine ? 'sent' : 'received'}`}>
                <div className="message-bubble">
                  <p className="message-text">{msg.content}</p>
                  <span className="message-time">
                    {formatTime(msg.created_at)}
                    {isMine && (
                      <span className={`message-status ${msg.is_read ? 'read' : ''}`}>
                        {msg.is_read ? ' \u2713\u2713' : ' \u2713'}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write a message..."
          rows={1}
          maxLength={2000}
        />
        <button
          onClick={handleSend}
          disabled={!newMessage.trim() || sending}
          className="send-btn"
        >
          {sending ? '...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default Chat;
